import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  BookOpen,
  HelpCircle,
  FileText,
  Phone,
  Mail,
} from "lucide-react";
import MainLayout from "../../components/layout/MainLayout";
import Card from "../../components/common/Card";

/**
 * SupportMainPage - 고객지원 메인 페이지
 * 공지사항, FAQ, 문의하기, 사용설명서로 이동할 수 있는 메인 허브
 */
const SupportMainPage = () => {
  const navigate = useNavigate();

  const supportMenus = [
    {
      title: "공지사항",
      description: "서비스 업데이트 및 중요 공지사항을 확인하세요",
      icon: FileText,
      path: "/support/notices",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: "FAQ",
      description: "자주 묻는 질문과 답변을 확인하세요",
      icon: HelpCircle,
      path: "/support/faq",
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      title: "1:1 문의",
      description: "궁금한 사항을 문의해주세요",
      icon: MessageSquare,
      path: "/support/inquiry",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      title: "사용 설명서",
      description: "서비스 이용 방법을 자세히 안내합니다",
      icon: BookOpen,
      path: "/support/guide",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleMenuKeyDown = (e, path) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(path);
    }
  };

  // 영업중/마감 실시간 판단 (평일 09:00-18:00, KST 기준 근사)
  const now = new Date();
  const day = now.getDay(); // 0:일 ~ 6:토
  const hour = now.getHours();
  const isOpenNow = day >= 1 && day <= 5 && hour >= 9 && hour < 18;

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* 페이지 헤더 */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-3">
            고객지원
          </h1>
          <p className="text-accent-600 text-lg">무엇을 도와드릴까요?</p>
        </div>

        {/* 지원 메뉴 그리드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {supportMenus.map((menu) => (
            <Card
              key={menu.path}
              className="bg-white cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 focus-within:ring-2 focus-within:ring-primary-500"
              onClick={() => handleMenuClick(menu.path)}
            >
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => handleMenuKeyDown(e, menu.path)}
                aria-label={menu.title}
                className="text-center p-4 focus:outline-none"
              >
                {/* 아이콘 */}
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${menu.bgColor} mb-4`}
                >
                  <menu.icon className={menu.color} size={32} />
                </div>

                {/* 제목 */}
                <h2 className="text-xl font-bold text-primary mb-2">
                  {menu.title}
                </h2>

                {/* 설명 */}
                <p className="text-sm text-accent-600">{menu.description}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* 추가 안내 */}
        <div className="mt-12 max-w-4xl mx-auto">
          <Card className="bg-accent-50 border border-accent-200">
            <div className="text-center p-6">
              <h3 className="text-lg font-bold text-primary mb-2">
                💡 빠른 도움말
              </h3>
              <p className="text-accent-700 mb-4">
                먼저 FAQ에서 자주 묻는 질문을 확인해보세요.
                <br />
                원하는 답변을 찾지 못하셨다면 1:1 문의를 이용해주세요.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate("/support/faq")}
                  className="px-6 py-2 bg-white border-2 border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-white transition-colors"
                >
                  FAQ 보기
                </button>
                <button
                  onClick={() => navigate("/support/inquiry")}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  문의하기
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* 연락 카드 (tel / mailto) */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-4xl mx-auto">
          <a
            href="tel:1588-0000"
            className="flex items-center gap-3 rounded-lg border border-accent-200 bg-white p-4 transition-all hover:shadow-md"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Phone size={20} />
            </span>
            <span>
              <span className="block text-sm font-semibold text-primary">
                전화 문의
              </span>
              <span className="block text-sm text-accent-600">1588-0000</span>
            </span>
          </a>
          <a
            href="mailto:support@funeral-service.com"
            className="flex items-center gap-3 rounded-lg border border-accent-200 bg-white p-4 transition-all hover:shadow-md"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600">
              <Mail size={20} />
            </span>
            <span>
              <span className="block text-sm font-semibold text-primary">
                이메일 문의
              </span>
              <span className="block text-sm text-accent-600">
                support@funeral-service.com
              </span>
            </span>
          </a>
        </div>

        {/* 운영 시간 안내 */}
        <div className="mt-8 text-center">
          <p className="text-sm text-accent-600">
            <span className="font-semibold">고객센터 운영시간:</span> 평일 09:00
            - 18:00 (주말 및 공휴일 휴무)
            <span
              className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                isOpenNow
                  ? "bg-green-100 text-green-800"
                  : "bg-accent-100 text-accent-700"
              }`}
            >
              {isOpenNow ? "● 영업중" : "● 영업 마감"}
            </span>
          </p>
          <p className="text-sm text-accent-600 mt-1">
            1:1 문의는 24시간 접수 가능하며, 순차적으로 답변 드립니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default SupportMainPage;
